import React, { useState } from 'react';
import { Check, Zap, ArrowRight, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Pricing.css';

const PLANS = [
  {
    name: 'Free',
    price: { monthly: 0, annual: 0 },
    currency: '',
    desc: 'For solo builders exploring what\'s out there.',
    cta: 'Get started',
    ctaLink: '/signup',
    highlight: false,
    features: [
      '50 AI searches / month',
      'Access to 1,000 tools',
      'Basic comparison (3 tools)',
      'Community reviews',
      'Browser extension',
    ],
  },
  {
    name: 'Pro',
    price: { monthly: 30400, annual: 22400 },
    currency: '₦',
    desc: 'For founders and product builders who move fast.',
    cta: 'Start Pro free',
    ctaLink: '/signup',
    highlight: true,
    badge: 'Most popular',
    features: [
      'Unlimited AI searches',
      'Full catalog (12,000+ tools)',
      'Advanced comparison (10 tools)',
      'Integration graph',
      'Saved stacks (unlimited)',
      'AI-powered stack audit',
      'Priority support',
      'Slack alerts on new tools',
    ],
  },
  {
    name: 'Team',
    price: { monthly: 78400, annual: 60800 },
    currency: '₦',
    desc: 'For teams who want to align on their tool stack.',
    cta: 'Start Team trial',
    ctaLink: '/signup',
    highlight: false,
    features: [
      'Everything in Pro',
      'Up to 10 seats',
      'Shared team stacks',
      'Admin & permission controls',
      'Usage analytics',
      'SSO / SAML',
      'Dedicated onboarding',
      'SLA support',
    ],
  },
];

const FAQS = [
  { q: 'Is there a free trial for Pro?', a: 'Yes — all paid plans include a 14-day free trial. No credit card required to start.' },
  { q: 'Can I switch plans later?', a: 'Absolutely. You can upgrade, downgrade, or cancel anytime from your settings. Proration is handled automatically.' },
  { q: 'What counts as an AI search?', a: 'Any natural-language query that triggers our AI recommendation engine. Browsing the catalog or filtering doesn\'t count.' },
  { q: 'Do you offer discounts for startups?', a: 'Yes. We have a startup program offering 50% off Pro for qualifying early-stage companies. Email us at startups@toolmind.ai.' },
  { q: 'Is my data private?', a: 'Your saved stacks and search history are private by default. We never sell your data or use it to train external models.' },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <main style={{ paddingTop: 'var(--nav-h)' }}>
      {/* Header */}
      <section className="section">
        <div className="section-inner" style={{ textAlign: 'center' }}>
          <div className="section-label" style={{ margin: '0 auto var(--sp-4)' }}><Zap size={12} /> Pricing</div>
          <h1 className="section-title">Simple pricing.<br /><span className="grad-text">No surprises.</span></h1>
          <p className="section-sub" style={{ margin: '0 auto var(--sp-8)' }}>
            Start free. Scale when you need it. Every plan includes a 14-day Pro trial.
          </p>

          {/* Toggle */}
          <div className="pricing__toggle glass">
            <button
              className={`pricing__toggle-btn ${!annual ? 'active' : ''}`}
              onClick={() => setAnnual(false)}
            >Monthly</button>
            <button
              className={`pricing__toggle-btn ${annual ? 'active' : ''}`}
              onClick={() => setAnnual(true)}
            >
              Annual
              <span className="pricing__save-badge">Save 25%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section style={{ padding: '0 var(--sp-6) var(--sp-20)' }}>
        <div className="section-inner">
          <div className="pricing__grid">
            {PLANS.map(plan => (
              <div
                key={plan.name}
                className={`pricing-card glass ${plan.highlight ? 'pricing-card--hi' : ''}`}
              >
                {plan.highlight && <div className="pricing-card__glow" aria-hidden />}
                {plan.badge && <div className="pricing-card__badge">{plan.badge}</div>}

                <div className="pricing-card__header">
                  <div className="pricing-card__name">{plan.name}</div>
                  <div className="pricing-card__price">
                    {plan.price.monthly === 0 ? (
                      <span className="pricing-card__amount">Free</span>
                    ) : (
                      <>
                        <span className="pricing-card__amount">
                          {plan.currency}{(annual ? plan.price.annual : plan.price.monthly).toLocaleString('en-NG')}
                        </span>
                        <span className="pricing-card__period">/mo</span>
                      </>
                    )}
                  </div>
                  {plan.price.monthly > 0 && annual && (
                    <div className="pricing-card__billed">Billed annually</div>
                  )}
                  {plan.price.monthly === 0 && (
                    <div className="pricing-card__billed">Free forever</div>
                  )}
                  <p className="pricing-card__desc">{plan.desc}</p>
                </div>

                <Link
                  to={plan.ctaLink}
                  className={`btn ${plan.highlight ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ justifyContent: 'center' }}
                >
                  {plan.cta} <ArrowRight size={16} />
                </Link>

                <div className="pricing-card__divider" />

                <ul className="pricing-card__features">
                  {plan.features.map(f => (
                    <li key={f} className="pricing-card__feature">
                      <div className={`pricing-card__check ${plan.highlight ? 'pricing-card__check--hi' : ''}`}>
                        <Check size={12} />
                      </div>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Enterprise row */}
          <div className="pricing__enterprise glass-hi">
            <div>
              <div className="pricing__enterprise-title">Enterprise</div>
              <p className="pricing__enterprise-sub">Custom seats, dedicated infrastructure, compliance, and a named account manager.</p>
            </div>
            <a href="mailto:sales@toolmind.ai" className="btn btn-ghost">
              Talk to sales <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section">
        <div className="section-inner pricing__faq-wrap">
          <div style={{ textAlign: 'center', marginBottom: 'var(--sp-12)' }}>
            <div className="section-label" style={{ margin: '0 auto var(--sp-4)' }}><HelpCircle size={12} /> FAQ</div>
            <h2 className="section-title">Questions? <span className="grad-text">We've got answers.</span></h2>
          </div>
          <div className="pricing__faqs">
            {FAQS.map(({ q, a }, i) => (
              <div key={q} className={`pricing__faq glass ${openFaq === i ? 'open' : ''}`}>
                <button
                  className="pricing__faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{q}</span>
                  <span className="pricing__faq-arrow">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="pricing__faq-a">{a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
